import React, { useEffect, useState } from "react";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import photo from "./23.jpg";
import ProgressPhotos from "../../components/common/ProgressPhotos";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getProjectById,
  getProjectWorkers,
  getTaskByProject,
  getWorkerProgress,
  getUpdateProjectStatus,
  createClient,
  updateClient,
  getClientDetail,
} from "../../services/service";
import {
  Clockicon,
  DollarIcon,
  FilterButton,
  NextArrow,
  SelectedRadio,
  TotalProjects,
  TotalWorkers,
  UnselectedRadio,
} from "../../assets/icons";
import TasksBar from "../../components/common/TasksBar";
import WorkersBar, { formatWorkedDuration } from "../../components/common/WorkersBar";
import WorkerHoursReport from "../Workers/WorkerHoursReport";
import AssignWorkerBar from "../../components/common/AssignWorkerBar";
import { Dropdown, Menu, Select, Button, Spin, Modal, Drawer, message } from "antd";
import CreateTaskSidebar from "../../components/common/CreateTaskSideBar";
import { IMAGE_BASE_URL } from "../../config/api";
import { DropDown } from "../../assets/icons";
import Phone from "../../atoms/phone";
import { useSelector } from 'react-redux';
import ProgressCard from "../../components/common/ProgressCard";
import { UpgradeIcon } from "../../assets/icons";
import UpgradePromptModal from "../../components/common/UpgradePromptModal";
import { selectCurrentPlan } from '../../redux/selectors/authSelectors';
import { FEATURE_REQUIRED_PLANS } from '../../utils/planFeatureAccess';

const { Option: SelectOption } = Select;

const formatCommentDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "";
  try {
    return `${d.toLocaleDateString()} • ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } catch (e) {
    return "";
  }
};

const ViewProjectDetails = () => {
  // --- Project State ---
  const [loader, setLoader] = useState(true);
  const [projectData, setProjectData] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState("");
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [projectProgress, setProjectProgress] = useState([]);
  const [isCompleteModalVisible, setCompleteModalVisible] = useState(false);
  const [isHoursReportOpen, setIsHoursReportOpen] = useState(false);

  // --- UI / Sidebar State ---
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [isWorkerbarVisible, setWorkerbarVisible] = useState(false);
  const [visibleDropdown, setVisibleDropdown] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // --- Workers State ---
  const [workersData, setWorkersData] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("Active");

  // --- Tasks State ---
  const [tasks, setTasks] = useState([]);

  // --- Comments State ---
  const [comments, setComments] = useState([]);
  const [commentFilterVisible, setCommentFilterVisible] = useState(false);
  const [commentFilter, setCommentFilter] = useState('All');
  const [commentSearchValue, setCommentSearchValue] = useState("");
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const [filteredComments, setFilteredComments] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [commentFilterCount, setCommentFilterCount] = useState(0);

  // --- Client State ---
  const [isClientModalVisible, setClientModalVisible] = useState(false);
  const [updatingClient, setUpdatingClient] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [clientFormErrors, setClientFormErrors] = useState({});
  const [clientFormTouched, setClientFormTouched] = useState({});
  const [clientExistingImage, setClientExistingImage] = useState(null);
  const [clientOriginalImage, setClientOriginalImage] = useState(null);
  const [clientImageRemoved, setClientImageRemoved] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    username: "",
    email: "",
    password: "",
    phoneNo: "",
    address: "",
    bio: "",
    image: null,
  });
  const location = useLocation();
  const { id } = useParams();
  const currentPlan = useSelector(selectCurrentPlan);

  const resetClientForm = () => {
    setNewClientForm({
      username: "",
      email: "",
      password: "",
      phoneNo: "",
      address: "",
      bio: "",
      image: null,
    });
    setClientFormErrors({});
    setClientFormTouched({});
    setClientExistingImage(null);
    setClientOriginalImage(null);
    setClientImageRemoved(false);
  };

  const validateClientForm = (formData, isUpdate = false) => {
    const errors = {};
    const trimmedUsername = formData.username?.trim();
    const trimmedEmail = formData.email?.trim();
    const trimmedPhoneNo = formData.phoneNo?.trim();
    const trimmedAddress = formData.address?.trim();
    const trimmedPassword = formData.password?.trim();
    const normalizedPhone = trimmedPhoneNo?.replace(/\D/g, "") || "";

    if (!trimmedUsername) {
      errors.username = "Username is required";
    }

    if (!trimmedEmail) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      errors.email = "Please enter a valid email";
    }

    if (!isUpdate) {
      if (!trimmedPassword) {
        errors.password = "Password is required";
      } else if (trimmedPassword.length < 8) {
        errors.password = "Password must be at least 8 characters";
      } else if (!/[A-Z]/.test(trimmedPassword)) {
        errors.password = "Password must include at least one uppercase letter";
      } else if (!/[a-z]/.test(trimmedPassword)) {
        errors.password = "Password must include at least one lowercase letter";
      } else if (!/\d/.test(trimmedPassword)) {
        errors.password = "Password must include at least one number";
      } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(trimmedPassword)) {
        errors.password = "Password must include at least one special character";
      }
    }

    if (!trimmedPhoneNo) {
      errors.phoneNo = "Phone number is required";
    } else if (normalizedPhone.length < 7) {
      errors.phoneNo = "Please enter a valid phone number";
    }

    if (!trimmedAddress) {
      errors.address = "Address is required";
    }

    return errors;
  };

  const handleClientFieldChange = (field, value) => {
    setNewClientForm((prev) => ({ ...prev, [field]: value }));
    if (clientFormTouched[field]) {
      const isUpdate = Boolean(projectData?.Client?.id);
      const nextErrors = validateClientForm({ ...newClientForm, [field]: value }, isUpdate);
      setClientFormErrors(nextErrors);
    }
  };

  const handleClientFieldBlur = (field) => {
    setClientFormTouched((prev) => ({ ...prev, [field]: true }));
    const isUpdate = Boolean(projectData?.Client?.id);
    const nextErrors = validateClientForm(newClientForm, isUpdate);
    setClientFormErrors(nextErrors);
  };

  const canAccessFeature = (feature) => {
    const planLevels = {
      'trial': 0,
      'basic': 1,
      'premium': 2,
      'ultimate': 3
    };
    return planLevels[currentPlan] >= planLevels[FEATURE_REQUIRED_PLANS[feature]];
  };

  const handleDropdownVisibleChange = (flag) => {
    setVisibleDropdown(flag);
  };
  const getMenu = () => (
    <Menu>
      <div className="w-[200px] p-4 bg-white rounded-lg shadow-md">
        <p className="font-semibold text-gray-700 mb-2">Status</p>
        <div className="flex flex-col gap-3">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="status"
              value="Active"
              checked={projectData?.status === true}
              onChange={handleStatusChange}
              className="hidden"
            />
            <span className="inline-flex w-6 h-6 items-center justify-center [&>svg]:w-6 [&>svg]:h-6">
              {projectData?.status === true ? SelectedRadio : UnselectedRadio}
            </span>
            <span className="ml-3 text-gray-700 font-medium">Active</span>
          </label>

          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="status"
              value="Pause"
              checked={projectData?.status === false}
              onChange={handleStatusChange}
              className="hidden"
            />
            <span className="inline-flex w-6 h-6 items-center justify-center [&>svg]:w-6 [&>svg]:h-6">
              {projectData?.status === false ? SelectedRadio : UnselectedRadio}
            </span>
            <span className="ml-3 text-gray-700 font-medium">Pause</span>
          </label>
        </div>
      </div>
    </Menu>
  );

  const getCommentFilterMenu = () => (
    <Menu>
      <div className="p-4 -mt-2 -ml-3 w-[368px] rounded-[8px] bg-white filter-shadow">
        <div className="flex flex-col gap-3 font-medium text-[14px] text-[#4B5563]">
          <p>Filter By</p>
          <Select
            placeholder="Choose type"
            onChange={(value) => setSelectedFilter(value)}
            value={selectedFilter}
            className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none -mt-1"
            suffixIcon={DropDown}
          >
            <SelectOption value="All">All</SelectOption>
            <SelectOption value="manager">Manager</SelectOption>
            <SelectOption value="worker">Worker</SelectOption>
          </Select>

          <Button
            type="submit"
            onClick={() => {
              setCommentFilter(selectedFilter);
              setCommentFilterCount(selectedFilter === 'All' ? 0 : 1);
              setCommentFilterVisible(false);
            }}
            className="bg-[#F97316] border border-[#F97316] w-full h-[44px] font-medium text-[13px] text-white"
          >
            Apply
          </Button>
        </div>
      </div>
    </Menu>
  );

  const projectId = location.state?.projectId || id;
  const fetchProject = async () => {
    setLoader(true);
    try {
      const response = await getProjectById(projectId);
      if (response) {
        console.log(response, 'response');
        setProjectData(response?.data?.project);
        setComments(response?.data?.comments || []);
        const formattedPhotos = response?.data?.attachments?.map(attachment => ({
          url: `${IMAGE_BASE_URL}${attachment.url}`,
          commentCount: attachment.WPAttachmentComments?.length || 0,
          id: attachment.id,
          projectId,
          progress_id: attachment.progress_id
        })) || [];
        setProgressPhotos(formattedPhotos);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    if (projectId) {

      fetchProject();
    }
  }, [projectId, deleteSuccess]);

  const handleGetTasks = async () => {
    try {
      const response = await getTaskByProject(projectId, "", 1, 100);

      if (response?.success) {
        setTasks(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching tasks data:", error);
    }
  };

  useEffect(() => {
    handleGetTasks();
  }, [isSidebarVisible, projectId, deleteSuccess]);

  const handleGetWorkers = async () => {
    fetchProject();
    try {
      const response = await getProjectWorkers(projectId);
      if (response?.success) {
        setWorkersData(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching workers data:", error);
    }
  };

  useEffect(() => {
    handleGetWorkers();
  }, [projectId]);

  useEffect(() => {
    const loadClientForEdit = async () => {
      if (!isClientModalVisible) return;

      if (!projectData?.Client?.id) {
        resetClientForm();
        return;
      }

      try {
        const response = await getClientDetail(projectData.Client.id);
        const detail = response?.data?.client || response?.data || projectData?.Client;

        setNewClientForm({
          username: detail?.username || "",
          email: detail?.email || "",
          password: "",
          phoneNo: detail?.phoneNo || "",
          address: detail?.address || "",
          bio: detail?.bio || "",
          image: null,
        });
        const existingImage = detail?.image || projectData?.Client?.image || null;
        setClientExistingImage(existingImage);
        setClientOriginalImage(existingImage);
        setClientImageRemoved(false);
      } catch (error) {
        console.error("Error fetching client detail:", error);
        setNewClientForm({
          username: projectData?.Client?.username || "",
          email: projectData?.Client?.email || "",
          password: "",
          phoneNo: projectData?.Client?.phoneNo || "",
          address: projectData?.Client?.address || "",
          bio: projectData?.Client?.bio || "",
          image: null,
        });
        const existingImage = projectData?.Client?.image || null;
        setClientExistingImage(existingImage);
        setClientOriginalImage(existingImage);
        setClientImageRemoved(false);
      }
    };

    loadClientForEdit();
  }, [isClientModalVisible, projectData?.Client?.id]);

  const handleMarkComplete = async () => {
    try {
      const response = await getUpdateProjectStatus({
        project_id: projectId,
        status: false, // false = Completed, true = Active/In Progress
      });
      if (response?.success) {
        message.success("Project marked as completed successfully");
        setCompleteModalVisible(false);
        fetchProject(); // Refresh project data
      } else {
        message.error(response?.message || "Failed to mark project as complete");
      }
    } catch (error) {
      console.error("Error marking project complete:", error);
      message.error("Failed to mark project as complete");
    }
  };

  const handleCreateOrUpdateClient = async () => {
    const isUpdate = Boolean(projectData?.Client?.id);
    const errors = validateClientForm(newClientForm, isUpdate);
    setClientFormErrors(errors);
    setClientFormTouched({
      username: true,
      email: true,
      password: !isUpdate,
      phoneNo: true,
      address: true,
    });

    if (Object.keys(errors).length > 0) {
      message.error("Please fix the highlighted fields");
      return;
    }

    const username = newClientForm.username.trim();
    const email = newClientForm.email.trim();
    const password = newClientForm.password.trim();

    if (!username || !email) return;

    try {
      if (isUpdate) {
        setUpdatingClient(true);
      } else {
        setCreatingClient(true);
      }

      const payload = new FormData();
      payload.append("username", username);
      payload.append("email", email);
      if (!isUpdate) {
        payload.append("password", password);
        payload.append("project_id", projectId);
      }
      if (newClientForm.phoneNo?.trim()) payload.append("phoneNo", newClientForm.phoneNo.trim());
      if (newClientForm.address?.trim()) payload.append("address", newClientForm.address.trim());
      if (newClientForm.bio?.trim()) payload.append("bio", newClientForm.bio.trim());
      if (newClientForm.image) payload.append("image", newClientForm.image);
      if (isUpdate && clientImageRemoved && !newClientForm.image) {
        payload.append("delete_image", "true");
      }

      const response = isUpdate
        ? await updateClient(projectData.Client.id, payload)
        : await createClient(payload);

      if (response?.success) {
        message.success(isUpdate ? "Client updated successfully" : "Client created and assigned successfully");
        resetClientForm();
        setClientModalVisible(false);
        await fetchProject();
      } else {
        message.error(response?.message || `Failed to ${isUpdate ? "update" : "create"} client`);
      }
    } catch (error) {
      console.error("Error saving client:", error);
      message.error(error?.response?.data?.message || "Failed to save client");
    } finally {
      setCreatingClient(false);
      setUpdatingClient(false);
    }
  };

  const Data = [
    {
      heading: "Project name",
      title: projectData?.title,
    },
    {
      heading: "Client name",
      title: (
        <div className="flex items-center justify-between w-full">
          {projectData?.Client ? (
            <div className="flex items-center gap-2">
              {projectData?.Client?.image && (
                <img
                  src={
                    projectData.Client.image.startsWith("http")
                      ? projectData.Client.image
                      : `${IMAGE_BASE_URL}${projectData.Client.image}`
                  }
                  alt="Client"
                  className="w-8 h-8 rounded-full object-cover"
                />
              )}
              <span>{projectData?.Client?.username}</span>
            </div>
          ) : (
            <span className="text-gray-400 italic">Not Assigned</span>
          )}
          <button
            onClick={() => setClientModalVisible(true)}
            className="text-[#F97316] text-[13px] font-medium hover:underline ml-1"
          >
            {projectData?.Client ? "Change" : "Add Client"}
          </button>
        </div>
      ),
    },
    {
      heading: "Start date",
      title: projectData?.expectedStartDate?.slice(0, 10) ?? "N/A",
    },
    {
      heading: "Location",
      title: projectData?.address ?? "N/A",
    },
    {
      heading: "Expense / Project budget",
      title: `$${projectData?.total_expense ?? 0} / ${projectData?.budget ?? "N/A"}`,
    },
    {
      heading: "Estimated completion date",
      title: projectData?.expectedCompletionDate?.slice(0, 10) ?? "N/A",
    },
    {
      heading: "Area",
      title: projectData?.total_area ?? "N/A",
    },
    {
      heading: "Project Status",
      title: (() => {
        const rawStatus = projectData?.project_status || "N/A";
        return rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
      })(),
    },
  ];
  const data1 = [
    {
      id: 1,
      text: `$${projectData?.total_expense}` ?? "",
      total: "Expenses",
      icon: DollarIcon,
      onClick: () => navigate('/expenses', {
        state: { projectId: projectData?.id }
      }),
    },
    {
      id: 2,
      text: projectData?.worker_count ?? "",
      total: "Workers",
      icon: TotalWorkers,
      onClick: () => navigate('/view-worker', {
        state: { projectId: projectData?.id }
      }),
    },
    {
      id: 3,
      text: (() => {
        const rawStatus = projectData?.project_status || "-";
        return rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();
      })(),
      total: "Status",
      icon: TotalProjects,
    },
    {
      id: 4,
      text: formatWorkedDuration(
        (workersData?.AllocatedUsers || [])
          .filter((user) => !user?.projectAllocation?.deleted)
          .reduce((sum, user) => sum + (Number(user?.total_worked_seconds) || 0), 0)
      ),
      total: "Time Spent",
      icon: Clockicon,
      onClick: () => setIsHoursReportOpen(true),
    },
  ];

  const navigate = useNavigate();



  const toggleBio = () => {
    setIsBioExpanded(!isBioExpanded);
  };

  const handleOpenSidebar = () => setSidebarVisible(true);
  const handleCloseSidebar = () => setSidebarVisible(false);
  const handleOpenWorkerbar = () => setWorkerbarVisible(true);
  const handleCloseWorkerbar = () => setWorkerbarVisible(false);
  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);
    setVisibleDropdown(false);

    try {
      const response = await getUpdateProjectStatus({
        project_id: projectData?.id
      });

      if (response?.success) {
        // Refresh project data to get updated status
        fetchProject();
      }
    } catch (error) {
      console.error("Error updating project status:", error);
    }
  };

  const handleSendProgress = () => {
    navigate('/project-progress', { state: { projectId: projectId } });
  };

  const handleViewAllProgress = () => {
    navigate('/worker-progress', {
      state: {
        fromProject: true,
        projectId: projectData?.id,
        projectTitle: projectData?.title
      }
    });
  };



  useEffect(() => {
    const userDataString = localStorage.getItem('userData');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        const userId = userData?.user?.id;
        setLoggedInUserId(userId);
        console.log('User data:', userData);
        console.log('Set logged in user ID:', userId);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (!comments || !Array.isArray(comments)) {
      setFilteredComments([]);
      return;
    }

    if (commentFilter === 'All') {
      setFilteredComments(comments);
      return;
    }

    console.log('Filtering comments:', {
      comments,
      loggedInUserId,
      commentFilter
    });

    const filtered = comments.filter(comment => {
      // For debugging
      console.log('Checking comment:', {
        commentId: comment.id,
        created_by: comment.created_by,
        loggedInUserId,
        isManager: Number(comment.created_by) === Number(loggedInUserId)
      });

      if (commentFilter === 'manager') {
        return Number(comment.created_by) === Number(loggedInUserId);
      }
      if (commentFilter === 'worker') {
        return Number(comment.created_by) !== Number(loggedInUserId);
      }
      return true;
    });

    console.log('Filtered results:', filtered);
    setFilteredComments(filtered);
  }, [comments, commentFilter, loggedInUserId]);

  const fetchProjectProgress = async () => {
    try {
      const response = await getWorkerProgress({
        project_id: projectId,
        limit: 10,
        page: 1
      });

      if (response?.data) {
        setProjectProgress(response.data);
      }
    } catch (error) {
      console.error("Error fetching project progress:", error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchProjectProgress();
    }
  }, [projectId]);

  const ProjectProgress = ({ progressData }) => {
    const navigate = useNavigate();
    const safeWorkersData = Array.isArray(workersData)
      ? workersData
      : Array.isArray(workersData?.AllocatedUsers)
      ? workersData.AllocatedUsers
      : Array.isArray(workersData?.data)
      ? workersData.data
      : Array.isArray(workersData?.workers)
      ? workersData.workers
      : [];

    const workerNameById = safeWorkersData.reduce((acc, w) => {
      if (w?.id && w?.username) acc[String(w.id)] = w.username;
      return acc;
    }, {});

    const handleViewProgress = (id) => {
      navigate("/progress-detail", {
        state: {
          progressId: id
        },
      });
    };

    return (
      <div className="flex flex-col gap-3">
        {progressData?.slice(0, 10).map((progress, index) => (
          <div key={progress?.id || index} className="w-full">
            <ProgressCard
              progress={progress}
              workerNameById={workerNameById}
              onProgressClick={handleViewProgress}
            />
          </div>
        ))}

        {(!progressData || progressData.length === 0) && (
          <div className="text-center text-gray-600 py-4">
            No progress data available.
          </div>
        )}
      </div>
    );
  };

  const handleCommentAction = () => {
    if (!canAccessFeature('commenting')) {
      setShowUpgradeModal(true);
      return;
    }
    // Continue with comment action
  };

  return (
    <div className="w-full flex flex-col gap-6 pb-12">
      <div className="col-12">
        <div className="row d-flex align-items-end">
          <div className="col-12">
            <ListHeader
              mainHeading="Project"
              placeholder="Search"
              btnShow={true}
              linkBreadCrum="/view-project"
              blinkBreadCrumText="Project"
              linkBreadCrum1="/view-project"
              blinkBreadCrumText2="View detail"
              blinkBreadCrumText1="View project"
              searchShow={false}
              showLikeFilter={false}
              DropDown={false}
            />
          </div>
        </div>
      </div>
      {
        loader ? <Spin /> :
          <>
            <div className="w-[98%] grid grid-cols-4 gap-5">
              {data1?.map(({ text, total, icon, onClick }) => {
                return (
                  <div className="w-full">
                    <div className="rounded-[8px] w-full h-[100px] bg-white flex items-center justify-between">
                      <div className="px-3 w-100 ">
                        <div className="d-flex justify-content-between  text-center align-items-center ">
                          <div className="dashboard_icon">{icon}</div>
                          <div className="flex flex-col items-end">
                            <p className="font-medium !text-[28px] !text-[#4B5563]">
                              {text}
                            </p>
                            {total === "Status" ? (
                              <Dropdown
                                overlay={getMenu()}
                                trigger={["click"]}
                                visible={visibleDropdown}
                                getPopupContainer={() => document.body}
                                motion={null}
                                onVisibleChange={handleDropdownVisibleChange}
                              >
                                <p className="font-normal underline cursor-pointer text-[14px] text-[#B7B7B7]">
                                  {total}
                                </p>
                              </Dropdown>
                            ) : (
                              <p
                                onClick={onClick}
                                className="font-normal underline cursor-pointer text-[14px] text-[#B7B7B7]"
                              >
                                {total}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="w-[98%] flex items-center gap-5">
              <div className="w-[60%] min-h-[444px] rounded-[8px] bg-white p-3">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-[20px] font-semibold text-[#1E3A8A]">
                    {projectData?.title}
                  </h2>
                  {projectData?.status === true && (
                    <button
                      onClick={() => setCompleteModalVisible(true)}
                      className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded-[4px] font-medium text-[14px] transition-colors"
                    >
                      Mark as Complete
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {Data?.map((data, index) => (
                    <div key={index} className="flex flex-col items-start gap-2">
                      <p className="font-semibold text-[14px] text-black ">
                        {data?.heading}
                      </p>
                      <p className="font-normal text-[14px] text-black">
                        {data?.title}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col items-start gap-2 w-full mt-3">
                  <p className="font-semibold text-[14px] text-black ">Description</p>

                  <p className="font-normal text-[14px] text-black">
                    {isBioExpanded
                      ? projectData?.description
                      : `${projectData?.description?.substring(0, 1000)}`}
                    {projectData?.description?.length > 1000 && (
                      <button
                        onClick={toggleBio}
                        className="text-blue-600 hover:underline"
                      >
                        {isBioExpanded ? "Show Less" : "...Show More"}
                      </button>
                    )}
                  </p>
                </div>

                {projectData?.image && (
                  <div className="flex flex-col items-start gap-2 w-full mt-4">
                    <p className="font-semibold text-[14px] text-black">Project Image</p>
                    <div className="w-[150px] h-[150px] rounded-md overflow-hidden border border-gray-300 shadow-sm">
                      <img
                        src={
                          projectData.image.startsWith('http')
                            ? projectData.image
                            : `${IMAGE_BASE_URL}${projectData.image}`
                        }
                        alt="Project"
                        className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onError={(e) => {
                          e.target.parentElement.style.display = 'none';
                        }}
                        onClick={() => {
                          window.open(
                            projectData.image.startsWith('http')
                              ? projectData.image
                              : `${IMAGE_BASE_URL}${projectData.image}`,
                            '_blank'
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="w-[40%] h-[444px] rounded-[8px] bg-white overflow-y-scroll">
                <TasksBar
                  handleOpenSidebar={handleOpenSidebar}
                  tasks={tasks}
                  setDeleteSuccess={setDeleteSuccess}
                  deleteSuccess={deleteSuccess}
                />
              </div>
            </div>

            <div className="w-[98%] flex items-center gap-5 ">
              <div className="w-[60%] min-h-[444px] rounded-[8px] bg-white p-3">
                <div className="w-full flex items-center justify-between">
                  <p className="font-semibold text-base text-[#1E3A8A]">
                    Progress photos
                  </p>
                  <span
                    // onClick={() => {
                    //   navigate("/progress-detail", {
                    //     state: { 
                    //       photos: progressPhotos,
                    //       progressId: 111
                    //     },
                    //   });
                    // }}
                    onClick={handleViewAllProgress}
                    className="cursor-pointer"
                  >
                    {NextArrow}
                  </span>
                </div>
                <ProgressPhotos photos={progressPhotos} />
                {progressPhotos.length > 0 && <button
                  onClick={handleSendProgress}
                  disabled={progressPhotos.length === 0}
                  className="w-[126px] h-[32px] rounded-[4px] bg-[#F97316] font-medium text-[13px] text-white mt-4 "
                >
                  Send Progress
                </button>}
              </div>

              <div className="w-[40%] h-[460px] rounded-[8px] p-3 bg-white overflow-y-scroll">
                <div className="w-full flex items-center justify-between">
                  <p className="font-semibold text-base text-[#1E3A8A]">Comments</p>
                  <Dropdown
                    overlay={getCommentFilterMenu()}
                    trigger={["click"]}
                    placement="bottomRight"
                    visible={commentFilterVisible}
                    getPopupContainer={() => document.body}
                    transitionName=""
                    motion={null}
                    onVisibleChange={(visible) => {
                      if (!canAccessFeature('commenting')) {
                        setShowUpgradeModal(true);
                        return;
                      }
                      setCommentFilterVisible(visible);
                    }}
                  >
                    <span className={`cursor-pointer relative ${!canAccessFeature('commenting') ? 'opacity-50' : ''}`}>
                      {FilterButton}
                      {commentFilterCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-[#F97316] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                          {commentFilterCount}
                        </span>
                      )}
                    </span>
                  </Dropdown>
                </div>

                {!canAccessFeature('commenting') ? (
                  <div className="flex flex-col items-center justify-center h-[400px] text-center">
                    <p className="text-gray-500 mb-2">Comments are available in Premium plan</p>
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="text-[#F97316] hover:underline"
                    >
                      Upgrade to Premium
                    </button>
                  </div>
                ) : (
                  <div className="w-full grid grid-cols-1 gap-3 mt-3">
                    {filteredComments?.length > 0 ? (
                      filteredComments.map((comment) => {
                        console.log('Rendering comment:', {

                          created_by: comment.created_by,
                          isManager: Number(comment.created_by) === Number(loggedInUserId)
                        });

                        return (
                          <div
                            key={comment.id}
                            className="w-full rounded-[4px] min-h-[108px] border border-[#E9E9E9] bg-[#F8F9FB] p-[10px] flex flex-col gap-2 shadow-sm"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <img
                                  src={
                                    comment.User?.image
                                      ? (comment.User.image.startsWith('http')
                                        ? comment.User.image
                                        : `${IMAGE_BASE_URL}${comment.User.image}`)
                                      : "https://pnghq.com/wp-content/uploads/2023/02/employee-photo-placeholder-png-1000.png"
                                  }
                                  alt="profile"
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-normal text-[14px] text-[#4B5563]">
                                      {comment.User?.username || "Anonymous"}
                                    </p>
                                    <span className="bg-[#DEE2ED] text-[#1E3A8A] text-[10px] font-medium px-2 py-0.5 rounded">
                                      {comment.created_by && Number(comment.created_by) === Number(loggedInUserId) ? 'Manager' : 'Worker'}
                                    </span>
                                  </div>
                                  <p className="font-normal text-[10px] text-[#B7B7B7]">
                                    {comment.ProgressComments?.title ? `${comment.ProgressComments.title} • ` : ""}
                                    {comment.ProgressComments?.location ? `${comment.ProgressComments.location} • ` : ""}
                                    {comment.createdAt ? formatCommentDate(comment.createdAt) : ""}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <p className="text-[#4B5563] text-[14px] font-normal ml-[50px]">
                              {comment.comment}
                            </p>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex flex-col   text-center">
                        <p className="text-gray-500">No comments available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="w-[98%] flex items-start gap-5">
              <div className="w-[60%] h-[600px] rounded-[8px] bg-white p-3 overflow-y-scroll">
                <div className="w-full flex items-center justify-between mb-4">
                  <p className="font-semibold text-base text-[#1E3A8A]">
                    Top 10 Project Progresses
                  </p>
                  <span
                    className="cursor-pointer"
                    onClick={handleViewAllProgress}
                  >
                    {NextArrow}
                  </span>
                </div>
                <div className="pr-2">
                  <ProjectProgress progressData={projectProgress} />
                </div>
              </div>

              <div className="w-[40%] h-[600px] rounded-[8px] p-3 bg-white overflow-y-scroll">
                <WorkersBar
                  handleOpenWorkerbar={handleOpenWorkerbar}
                  workersData={workersData}
                  refreshWorkers={handleGetWorkers}
                />
              </div>
            </div>
          </>
      }

      <CreateTaskSidebar
        visible={isSidebarVisible}
        onClose={handleCloseSidebar}
        projectId={projectData?.id}
        refreshWorkers={handleGetWorkers}
        from='projectDetail'
      />
      <AssignWorkerBar
        visible={isWorkerbarVisible}
        onClose={handleCloseWorkerbar}
        projectId={projectData?.id}
        refreshWorkers={handleGetWorkers}
      />

      <Drawer
        title={
          <span className="text-[17px] font-semibold text-[#1E3A8A]">
            {projectData?.Client ? "Change Client" : "Assign Client"}
          </span>
        }
        placement="right"
        width={430}
        bodyStyle={{ padding: "12px 16px 16px 16px", overflowX: "hidden" }}
        open={isClientModalVisible}
        onClose={() => {
          setClientModalVisible(false);
          resetClientForm();
        }}
        destroyOnClose
      >
        <div className="h-full flex flex-col py-1 px-1 overflow-x-hidden">
          <div className="flex-1 overflow-y-auto px-1">
            <div className="pt-4 mt-1">
              <div className="flex flex-col gap-3">
              <div>
                <label className="block text-[14px] font-normal text-[#4B5563] mb-1">Username</label>
                <input
                  type="text"
                  placeholder="Username"
                  value={newClientForm.username}
                  onChange={(e) => handleClientFieldChange("username", e.target.value)}
                  onBlur={() => handleClientFieldBlur("username")}
                  className={`w-full h-[52px] px-3 box-border border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${clientFormTouched.username && clientFormErrors.username
                    ? "border-red-500"
                    : "border-[#E9E9E9]"
                    }`}
                />
                {clientFormTouched.username && clientFormErrors.username && (
                  <p className="text-red-500 text-sm mt-1">{clientFormErrors.username}</p>
                )}
              </div>
              <div>
                <label className="block text-[14px] font-normal text-[#4B5563] mb-1">Email</label>
                <input
                  type="email"
                  placeholder="Email"
                  value={newClientForm.email}
                  onChange={(e) => handleClientFieldChange("email", e.target.value)}
                  onBlur={() => handleClientFieldBlur("email")}
                  className={`w-full h-[52px] px-3 box-border border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${clientFormTouched.email && clientFormErrors.email
                    ? "border-red-500"
                    : "border-[#E9E9E9]"
                    }`}
                />
                {clientFormTouched.email && clientFormErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{clientFormErrors.email}</p>
                )}
              </div>
              {!projectData?.Client?.id && (
                <div>
                  <label className="block text-[14px] font-normal text-[#4B5563] mb-1">Password</label>
                  <input
                    type="password"
                    placeholder="Password"
                    value={newClientForm.password}
                    onChange={(e) => handleClientFieldChange("password", e.target.value)}
                    onBlur={() => handleClientFieldBlur("password")}
                    className={`w-full h-[52px] px-3 box-border border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${clientFormTouched.password && clientFormErrors.password
                      ? "border-red-500"
                      : "border-[#E9E9E9]"
                      }`}
                  />
                  {clientFormTouched.password && clientFormErrors.password && (
                    <p className="text-red-500 text-sm mt-1">{clientFormErrors.password}</p>
                  )}
                </div>
              )}
              <div>
                <label className="block text-[14px] font-normal text-[#4B5563] mb-1">Phone No.</label>
                <Phone
                  name="phoneNo"
                  value={newClientForm.phoneNo}
                  handleChange={(value, name) => handleClientFieldChange(name, value)}
                  field={{
                    onBlur: () => handleClientFieldBlur("phoneNo"),
                  }}
                />
                {clientFormTouched.phoneNo && clientFormErrors.phoneNo && (
                  <p className="text-red-500 text-sm mt-1">{clientFormErrors.phoneNo}</p>
                )}
              </div>
              <div>
                <label className="block text-[14px] font-normal text-[#4B5563] mb-1">Address</label>
                <input
                  type="text"
                  placeholder="Address"
                  value={newClientForm.address}
                  onChange={(e) => handleClientFieldChange("address", e.target.value)}
                  onBlur={() => handleClientFieldBlur("address")}
                  className={`w-full h-[52px] px-3 box-border border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${clientFormTouched.address && clientFormErrors.address
                    ? "border-red-500"
                    : "border-[#E9E9E9]"
                    }`}
                />
                {clientFormTouched.address && clientFormErrors.address && (
                  <p className="text-red-500 text-sm mt-1">{clientFormErrors.address}</p>
                )}
              </div>
              <div>
                <label className="block text-[14px] font-normal text-[#4B5563] mb-1">Bio (optional)</label>
                <textarea
                  placeholder="Bio"
                  value={newClientForm.bio}
                  onChange={(e) => handleClientFieldChange("bio", e.target.value)}
                  className="w-full h-[120px] px-3 py-2 box-border border rounded border-[#E9E9E9] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] resize-none"
                />
              </div>
              <div>
                <label className="block text-[14px] font-normal text-[#4B5563] mb-1">Add profile image</label>
                <div className="flex flex-col gap-2">
                  {clientExistingImage && !newClientForm.image && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 border rounded p-2 flex items-center min-w-0">
                        <img
                          src={
                            clientExistingImage?.startsWith?.("http")
                              ? clientExistingImage
                              : `${IMAGE_BASE_URL}${clientExistingImage}`
                          }
                          alt="Client profile"
                          className="h-10 w-10 object-cover mr-2 rounded"
                        />
                        <span className="text-sm truncate flex-1">Current Profile Image</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setClientExistingImage(null);
                          setClientImageRemoved(true);
                        }}
                        className="text-red-500 hover:text-red-700 text-xl leading-none"
                        aria-label="Remove current image"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {(!clientExistingImage || newClientForm.image) && (
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          handleClientFieldChange("image", e.target.files?.[0] || null);
                          setClientExistingImage(null);
                          setClientImageRemoved(false);
                        }}
                        className="w-full h-[60px] p-2 box-border border rounded border-[#E9E9E9] focus:outline-none"
                      />
                      {newClientForm.image && (
                        <button
                          type="button"
                          onClick={() => {
                            handleClientFieldChange("image", null);
                            if (!clientImageRemoved && clientOriginalImage) {
                              setClientExistingImage(clientOriginalImage);
                            }
                          }}
                          className="text-red-500 hover:text-red-700 text-xl leading-none"
                          aria-label="Remove selected image"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 mt-2 bg-white">
            <Button
              onClick={() => {
                setClientModalVisible(false);
                resetClientForm();
              }}
              className="h-[44px] px-6 rounded-[4px] border border-[#D1D5DB] text-[#4B5563] font-medium"
            >
              Cancel
            </Button>
            <Button
              loading={updatingClient || creatingClient}
              disabled={
                updatingClient ||
                creatingClient ||
                Object.keys(validateClientForm(newClientForm, Boolean(projectData?.Client?.id))).length > 0
              }
              onClick={handleCreateOrUpdateClient}
              className="h-[44px] px-8 rounded-[4px] font-medium text-white"
              style={{
                backgroundColor:
                  Object.keys(validateClientForm(newClientForm, Boolean(projectData?.Client?.id))).length === 0
                    ? "#F97316"
                    : "#f5a96b",
                borderColor:
                  Object.keys(validateClientForm(newClientForm, Boolean(projectData?.Client?.id))).length === 0
                    ? "#F97316"
                    : "#f5a96b",
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </Drawer>

      <UpgradePromptModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        featureName="Client Management"
        requiredPlan="Premium"
      />

      {/* Mark Complete Confirmation Modal */}
      <Modal
        title="Mark Project as Complete"
        open={isCompleteModalVisible}
        onOk={handleMarkComplete}
        onCancel={() => setCompleteModalVisible(false)}
        okText="Yes, Mark Complete"
        cancelText="Cancel"
        okButtonProps={{
          style: {
            backgroundColor: '#F97316',
            borderColor: '#F97316',
            color: 'white',
          },
        }}
      >
        <p>Are you sure you want to mark this project as completed?</p>
        <p className="text-[#4B5563] text-[14px] mt-2">
          The project status will be changed from Active to Completed.
        </p>
      </Modal>

      <WorkerHoursReport
        open={isHoursReportOpen}
        onClose={() => setIsHoursReportOpen(false)}
        preSelectedWorkerIds={
          (workersData?.AllocatedUsers || [])
            .filter((user) => !user?.projectAllocation?.deleted)
            .map((user) => user?.id)
            .filter(Boolean)
        }
        heading={`Worker Hours Report — ${projectData?.title || "Project"}`}
        projectId={projectId}
      />

    </div>
  );
};

export default ViewProjectDetails;
